import { useCallback } from "react";
import { useEventsApi } from "./useEventsApi";
import { config } from "../config";
import type {
  AdminBacklogResponse,
  DevCommentBacklogResponse,
  DuplicateContractNameLatestHeadsResponse,
  DuplicateEventCodeVersionResponse,
  DuplicateLegacyEventCodeResponse,
  EmptyContractUserEventsResponse,
  PairIntegrityResponse,
} from "../shared/adminReports";
import type {
  BulkTranslateResponse,
  IncompleteTranslationsResponse,
} from "../shared/translationAdmin";

export const useAdminReportsApi = () => {
  const { callApi } = useEventsApi();

  const fetchUnapprovedBacklog = useCallback(
    async (params?: {
      limit?: number;
      event_type?: string | null;
      event_contract_name?: string | null;
    }): Promise<AdminBacklogResponse | undefined> => {
      const url = new URL(`${config.apiUrl}/api/events/admin-unapproved-backlog/`);
      if (params?.limit != null) url.searchParams.set("limit", String(params.limit));
      if (params?.event_type) url.searchParams.set("event_type", params.event_type);
      if (params?.event_contract_name) {
        url.searchParams.set("event_contract_name", params.event_contract_name);
      }
      return callApi(url.toString(), "GET");
    },
    [callApi]
  );

  const fetchDevCommentBacklog = useCallback(
    async (params?: {
      limit?: number;
      event_type?: string | null;
      event_contract_name?: string | null;
    }): Promise<DevCommentBacklogResponse | undefined> => {
      const url = new URL(`${config.apiUrl}/api/events/admin-dev-comment-backlog/`);
      if (params?.limit != null) url.searchParams.set("limit", String(params.limit));
      if (params?.event_type) url.searchParams.set("event_type", params.event_type);
      if (params?.event_contract_name) {
        url.searchParams.set("event_contract_name", params.event_contract_name);
      }
      return callApi(url.toString(), "GET");
    },
    [callApi]
  );

  const fetchDuplicateUserEventCodeVersion = useCallback(
    async (params?: {
      limit?: number;
    }): Promise<DuplicateEventCodeVersionResponse | undefined> => {
      const url = new URL(`${config.apiUrl}/api/events/admin-duplicate-user-event-code-version/`);
      if (params?.limit != null) url.searchParams.set("limit", String(params.limit));
      return callApi(url.toString(), "GET");
    },
    [callApi]
  );

  const fetchDuplicateLegacyEventCode = useCallback(
    async (params?: {
      limit?: number;
    }): Promise<DuplicateLegacyEventCodeResponse | undefined> => {
      const url = new URL(`${config.apiUrl}/api/events/admin-duplicate-legacy-event-code/`);
      if (params?.limit != null) url.searchParams.set("limit", String(params.limit));
      return callApi(url.toString(), "GET");
    },
    [callApi]
  );

  const fetchDuplicateContractNamesLatestHeads = useCallback(
    async (params?: {
      limit?: number;
    }): Promise<DuplicateContractNameLatestHeadsResponse | undefined> => {
      const url = new URL(
        `${config.apiUrl}/api/events/admin-duplicate-contract-names-latest-heads/`
      );
      if (params?.limit != null) url.searchParams.set("limit", String(params.limit));
      return callApi(url.toString(), "GET");
    },
    [callApi]
  );

  const fetchUserEventsEmptyContractLatest = useCallback(
    async (params?: {
      limit?: number;
      offset?: number;
      event_code?: number | string;
    }): Promise<EmptyContractUserEventsResponse | undefined> => {
      const url = new URL(
        `${config.apiUrl}/api/events/admin-user-events-empty-contract/`
      );
      if (params?.limit != null) url.searchParams.set("limit", String(params.limit));
      if (params?.offset != null) url.searchParams.set("offset", String(params.offset));
      if (params?.event_code != null && params.event_code !== "") {
        url.searchParams.set("event_code", String(params.event_code));
      }
      return callApi(url.toString(), "GET");
    },
    [callApi]
  );

  const fetchRisingResolvingIntegrity = useCallback(
    async (params?: {
      event_type?: string | null;
      event_contract_name?: string | null;
    }): Promise<PairIntegrityResponse | undefined> => {
      const url = new URL(
        `${config.apiUrl}/api/events/admin-rising-resolving-integrity/`
      );
      if (params?.event_type) url.searchParams.set("event_type", params.event_type);
      if (params?.event_contract_name) {
        url.searchParams.set("event_contract_name", params.event_contract_name);
      }
      return callApi(url.toString(), "GET");
    },
    [callApi]
  );

  const fetchIncompleteTranslations = useCallback(
    async (params?: {
      approved_only?: boolean;
      manufacturer_events?: boolean | null;
      offset?: number;
      limit?: number;
    }): Promise<IncompleteTranslationsResponse | undefined> => {
      const url = new URL(`${config.apiUrl}/api/events/admin-incomplete-translations/`);
      if (params?.approved_only) url.searchParams.set("approved_only", "true");
      if (params?.manufacturer_events === true) {
        url.searchParams.set("manufacturer_events", "true");
      } else if (params?.manufacturer_events === false) {
        url.searchParams.set("manufacturer_events", "false");
      }
      if (params?.offset != null) url.searchParams.set("offset", String(params.offset));
      if (params?.limit != null) url.searchParams.set("limit", String(params.limit));
      return callApi(url.toString(), "GET");
    },
    [callApi]
  );

  const bulkEnqueueTranslations = useCallback(
    async (globalEventIds: number[]): Promise<BulkTranslateResponse | undefined> => {
      return callApi(`${config.apiUrl}/api/events/admin-bulk-translate/`, "POST", {
        global_event_ids: globalEventIds,
      });
    },
    [callApi]
  );

  return {
    fetchUnapprovedBacklog,
    fetchDevCommentBacklog,
    fetchDuplicateUserEventCodeVersion,
    fetchDuplicateLegacyEventCode,
    fetchDuplicateContractNamesLatestHeads,
    fetchUserEventsEmptyContractLatest,
    fetchRisingResolvingIntegrity,
    fetchIncompleteTranslations,
    bulkEnqueueTranslations,
  };
};
